import { useEffect, useState } from "react";
import { Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "../../lib/api";
import { logError } from "../../lib/log";

const EDITION_ORDER = ["GENESIS", "SIGNATURE", "COLLECTOR", "CELESTIAL"];

export const PricingTab = () => {
  const [cfg, setCfg] = useState(null);
  const [history, setHistory] = useState([]);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadHistory = () =>
    api.get("/admin/pricing/history").then(({ data }) => setHistory(data))
      .catch((error) => logError("Could not load pricing history", error));

  useEffect(() => {
    api.get("/admin/pricing").then(({ data }) => setCfg(data))
      .catch((error) => { logError("Could not load pricing", error); toast.error("Could not load pricing"); });
    loadHistory();
  }, []);

  const setEdition = (edition, value) =>
    setCfg({ ...cfg, edition_prices: { ...cfg.edition_prices, [edition]: value } });

  const setLadder = (number, value) =>
    setCfg({ ...cfg, genesis_ladder: { ...cfg.genesis_ladder, [number]: value } });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        edition_prices: Object.fromEntries(EDITION_ORDER.map((e) => [e, Number(cfg.edition_prices[e]) || 0])),
        genesis_ladder: Object.fromEntries(
          Array.from({ length: 10 }, (_, i) => [String(i + 1), Number(cfg.genesis_ladder[String(i + 1)]) || 0])),
      };
      const { data } = await api.put("/admin/pricing", payload);
      setCfg({ ...cfg, ...data });
      await loadHistory();
      toast.success("Pricing updated — new registrations use these prices");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save pricing");
    } finally {
      setSaving(false);
    }
  };

  const rollback = async (version) => {
    try {
      const { data } = await api.post(`/admin/pricing/history/${version.version_id}/rollback`);
      setCfg({ ...cfg, ...data });
      await loadHistory();
      toast.success(`Rolled back to the pricing from ${version.changed_at.slice(0, 10)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not roll back");
    }
  };

  if (!cfg) return <Loader2 className="w-6 h-6 animate-spin text-amber-300 mt-12" />;

  return (
    <div className="grid lg:grid-cols-2 gap-8 mt-12" data-testid="admin-pricing">
      <div className="hy-card p-7">
        <div className="hy-label">Genesis ladder · registry #001–#010</div>
        <p className="text-xs text-slate-500 mt-3">Each founding record is priced individually, descending from #001.</p>
        <div className="space-y-3 mt-6">
          {Array.from({ length: 10 }, (_, i) => String(i + 1)).map((n) => (
            <div key={n} className="flex items-center gap-4">
              <span className="font-mono-hy text-xs text-amber-200 w-16">#{n.padStart(3, "0")}</span>
              <input type="number" min="1" value={cfg.genesis_ladder[n]} onChange={(e) => setLadder(n, e.target.value)}
                className="hy-input" data-testid={`pricing-genesis-${n}`} />
              <span className="text-xs text-slate-500 w-28 text-right">{money(Number(cfg.genesis_ladder[n]))}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="hy-card p-7 h-fit">
        <div className="hy-label">Edition pricing</div>
        <p className="text-xs text-slate-500 mt-3">
          Applies to every registry number in the band. The GENESIS value is the fallback if a ladder entry is missing.
        </p>
        <div className="space-y-4 mt-6">
          {EDITION_ORDER.map((e) => (
            <div key={e} className="flex items-center gap-4">
              <span className="text-xs uppercase tracking-widest text-slate-400 w-24">{e}</span>
              <input type="number" min="1" value={cfg.edition_prices[e]} onChange={(ev) => setEdition(e, ev.target.value)}
                className="hy-input" data-testid={`pricing-edition-${e}`} />
              <span className="text-xs text-slate-500 w-28 text-right">{money(Number(cfg.edition_prices[e]))}</span>
            </div>
          ))}
        </div>
        <button onClick={save} disabled={saving}
          className="hy-btn w-full py-3.5 text-xs uppercase mt-8 flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="pricing-save-btn">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save pricing
        </button>
        <p className="text-[0.7rem] text-slate-500 mt-5 leading-relaxed">
          Prices already quoted on unpaid records stay locked; changes affect new registrations only.
        </p>
      </div>

      <div className="hy-card p-7 lg:col-span-2" data-testid="pricing-history">
        <div className="hy-label">Change history</div>
        <p className="text-xs text-slate-500 mt-3">Who changed pricing, when, and what it was set to.</p>
        <div className="mt-6 space-y-3">
          {history.length === 0 && <p className="text-sm text-slate-500">No changes recorded yet.</p>}
          {(showAllHistory ? history : history.slice(0, 8)).map((h) => (
            <div key={h.version_id}
              className={`p-5 border flex flex-wrap items-center justify-between gap-4 ${h.is_current ? "border-amber-400/60 bg-amber-500/5" : "border-amber-500/15"}`}
              data-testid={`pricing-version-${h.version_id}`}>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-mono-hy text-xs text-amber-200">{h.changed_at.slice(0, 19).replace("T", " ")}</span>
                  <span className="text-[0.55rem] uppercase tracking-widest px-2 py-1 border border-amber-500/25 text-slate-400">
                    {h.reason}
                  </span>
                  {h.is_current && (
                    <span className="text-[0.55rem] uppercase tracking-widest text-emerald-400">Active</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  by {h.changed_by_email} · #001 {money(Number(h.genesis_ladder["1"]))} · SIG {money(h.edition_prices.SIGNATURE)}
                  {" "}· COL {money(h.edition_prices.COLLECTOR)} · CEL {money(h.edition_prices.CELESTIAL)}
                </div>
              </div>
              <button onClick={() => rollback(h)} disabled={h.is_current}
                className="hy-btn-ghost px-5 py-2.5 text-[0.65rem] uppercase flex items-center gap-2 disabled:opacity-40"
                data-testid={`pricing-rollback-${h.version_id}`}>
                <RotateCcw className="w-3.5 h-3.5" /> Roll back
              </button>
            </div>
          ))}
          {history.length > 8 && (
            <button onClick={() => setShowAllHistory(!showAllHistory)}
              className="hy-btn-ghost px-5 py-2.5 text-[0.65rem] uppercase" data-testid="pricing-history-toggle">
              {showAllHistory ? "Show fewer" : `Show all ${history.length} versions`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
